/** FLD-OFF — one outbox for every field-facing write (Install, Material, tickets, punch, photos,
 *  drawings). Records are append-only with a client UUID; the server treats a repeated id as a no-op.
 *  Statuses: pending | sending | accepted | adjusted | conflict | failed | lost | superseded |
 *  malformed (FLD-OFF-FIX-3: validated before send, quarantined instead of sent).
 *
 *  FLD-OFF-FIX-2 (2026-09-30):
 *  - a replayed multipart body is sent as multipart. The field client's default header is
 *    application/json; axios 1.x turns a FormData body into JSON under that header, which is why
 *    every replayed photo arrived with project_id and file "Field required".
 *  - a photo blob write is VERIFIED; if the device refuses it the photo is not queued silently.
 *  - a blob that is gone is `lost` (local condition, own field), never a "conflict"; nothing we
 *    do deletes a pending blob until the server confirms the file.
 *  - stale markup actions (server: superseded) are dropped with a log line, never asked about;
 *    queued moves of the same markup are deduped to the latest before replay.
 *  - retry policy: transient → automatic with backoff; rejected (validation) → only after the
 *    app changed (LOGIC_VERSION) or a manual Sync now; lost → never. */
import { idb, uuid, deviceId, storageEstimate } from "./db";
import { isManualOffline, probe } from "./quality";
import { classify, isLinkDown, noteFail, noteOk } from "./link";
import { enforceCap, persistStorage } from "./budget";

export const LOGIC_VERSION = 2;          // bump when a replay bug is fixed → rejected items get one more try
export const RETRYABLE = ["pending", "failed"];
export const WAITING = ["pending", "sending", "failed"];
if (typeof window !== "undefined") persistStorage();

const listeners = new Set();
export const onSync = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* ui */ } });

const state = { online: typeof navigator === "undefined" ? true : navigator.onLine, replaying: false, lastError: "" };
export const isOnline = () => !isManualOffline() && state.online && !isLinkDown();
export const setOnline = (v) => { if (state.online !== v) { state.online = v; emit(); if (v) replay(); } };
if (typeof window !== "undefined") {
  // FLD-OFF-FIX-3 §1 — `navigator.onLine` is a hint for coming BACK, never a reason to go
  // offline: Chromium reports false for a captive portal and for some VPN transitions. The
  // browser's "offline" event only asks the detector to check, it does not decide.
  window.addEventListener("online", () => { setOnline(true); wake("online-event"); });
  window.addEventListener("offline", () => { wake("offline-event"); });
  // iOS PWA: the `online` event frequently never fires after the app is resumed, so coming back
  // from the pocket is its own trigger — probe, then drain.
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") wake("foreground"); });
  window.addEventListener("focus", () => wake("focus"));
}

/** The only way back from "offline" that does not depend on a browser event we cannot trust. */
export async function wake(reason = "timer") {
  if (isManualOffline()) return false;
  if (state.online && !isLinkDown()) { replay(); return true; }
  const ok = await probe(clients.field);
  if (ok) { state.online = true; emit(); replay(); }
  return ok;
}

let clients = {};   // name → axios instance, registered by api modules
let hooks = {};     // name → { onAccepted, onConflict, onRemoved } — how a client keeps its local picture honest
export const registerClient = (name, instance, h = {}) => { clients[name] = instance; hooks[name] = h; };
const hook = async (rec, ev) => { try { await hooks[rec.client]?.[ev]?.(rec); } catch { /* local only */ } };

/** FormData cannot sit in IndexedDB as-is; blobs are stored beside the record. */
async function storeBlob(key, v) {
  await idb.set("blobs", key, v);
  if ((await idb.get("blobs", key))?.size === v.size) return true;
  await enforceCap();                                    // make room from cached views, never from pending items
  await idb.set("blobs", key, v);
  return (await idb.get("blobs", key))?.size === v.size;
}

async function serializeBody(body, id) {
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    const parts = [];
    let i = 0;
    for (const [k, v] of body.entries()) {
      if (v instanceof Blob) {
        const key = `${id}:${i++}`;
        if (!(await storeBlob(key, v))) throw Object.assign(new Error("This device has no room to keep the photo — clear cached views in Sync, then take it again"), { device_full: true });
        parts.push({ k, blob: key, name: v.name || "file", type: v.type, size: v.size });
      } else parts.push({ k, v: String(v) });
    }
    return { form: parts };
  }
  return { json: body ?? null };
}

async function deserializeBody(rec) {
  if (rec.body?.form) {
    const fd = new FormData();
    for (const p of rec.body.form) {
      if (p.blob) { const b = await idb.get("blobs", p.blob); if (!b) throw Object.assign(new Error("photo file missing on this device"), { lost: true }); fd.append(p.k, new File([b], p.name, { type: p.type })); }
      else fd.append(p.k, p.v);
    }
    return fd;
  }
  return rec.body?.json;
}

export async function enqueue({ id: given, client, method, url, body, project_id, kind, params, label }) {
  const id = given || uuid();
  const rec = { id, client, method: method.toUpperCase(), url, params: params || null, body: await serializeBody(body, id),
    project_id: project_id || "", kind: kind || "", label: label || "", status: "pending", attempts: 0,
    client_timestamp: new Date().toISOString(), user: localStorage.getItem("fieldops-installer-name") || "",
    seq: Date.now(), logic_version: LOGIC_VERSION };
  await idb.set("outbox", id, rec);
  emit();
  return rec;
}

export const outboxList = async () => (await idb.all("outbox")).sort((a, b) => a.seq - b.seq);
export const pendingCount = async () => (await idb.all("outbox")).filter((r) => WAITING.includes(r.status)).length;
export const conflictCount = async () => (await idb.all("outbox")).filter((r) => r.status === "conflict").length;
/** waiting / failed / lost reported SEPARATELY — "2 waiting of 10" hid 8 failed or lost photos. */
export const counts = (list) => ({
  waiting: list.filter((r) => ["pending", "sending"].includes(r.status)).length,
  failed: list.filter((r) => r.status === "failed").length,
  lost: list.filter((r) => r.status === "lost").length,
  malformed: list.filter((r) => r.status === "malformed").length,
  conflicts: list.filter((r) => r.status === "conflict").length,
  uploaded: list.filter((r) => ["accepted", "adjusted"].includes(r.status)).length,
});
export const retryAll = async () => {
  for (const r of await idb.all("outbox")) if (r.status === "failed") { r.status = "pending"; r.nextAt = 0; r.manual = true; await idb.set("outbox", r.id, r); }
  emit(); replay(true);
};
export const dismissLost = async () => { for (const r of await idb.all("outbox")) if (r.status === "lost") await removeRecord(r.id); };
export const clearUploaded = async () => { for (const r of await idb.all("outbox")) if (["accepted", "adjusted", "superseded"].includes(r.status)) await removeRecord(r.id); };

export async function removeRecord(id) {
  const rec = await idb.get("outbox", id);
  for (const p of rec?.body?.form || []) if (p.blob) await idb.del("blobs", p.blob);
  await idb.del("outbox", id);
  if (rec) await hook(rec, "onRemoved");
  emit();
}

export const retry = async (id) => { const r = await idb.get("outbox", id); if (r && !["lost", "malformed"].includes(r.status)) { r.status = "pending"; r.nextAt = 0; r.manual = true; await idb.set("outbox", id, r); emit(); replay(true); } };

/** Queued actions on the SAME markup (same PATCH url): keep the latest move, drop the ones it
 *  superseded — four carries of one markup used to replay as four conflicts. */
async function dedupe(list) {
  const latest = new Map();
  for (const r of list) if (r.kind === "markup" && r.method === "PATCH") { const key = `${r.method} ${r.url}`; if (!latest.has(key) || latest.get(key).seq < r.seq) latest.set(key, r); }
  const keep = [];
  for (const r of list) {
    const key = `${r.method} ${r.url}`;
    if (r.kind === "markup" && r.method === "PATCH" && latest.get(key) !== r) {
      console.info(`[outbox] dropped superseded markup action ${r.id} (${r.url}) — a later move of the same markup is queued`);
      await removeRecord(r.id);
    } else keep.push(r);
  }
  return keep;
}

/** What the installer should see for a server refusal — plain words, never JSON. */
const plainDetail = (data) => {
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg || ""}`).join("; ");
  if (d && typeof d === "object") return d.detail || d.message || "";
  return "";
};

/** Photos a record depends on go first; standalone photos last; everything else in order. */
function order(list) {
  const photoIds = new Set(list.filter((r) => r.kind === "photo").map((r) => r.id));
  const needed = new Set();
  list.forEach((r) => JSON.stringify(r.body || {}).replace(/local:([0-9a-f-]{36})/g, (_, pid) => { if (photoIds.has(pid)) needed.add(pid); return ""; }));
  const rank = (r) => (r.kind === "photo" ? (needed.has(r.id) ? 0 : 2) : 1);
  return [...list].sort((a, b) => rank(a) - rank(b) || a.seq - b.seq);
}

/** Replace `local:{photoId}` references with what the server gave back for that photo. */
async function resolveRefs(rec) {
  const txt = JSON.stringify(rec.body || {});
  if (!txt.includes("local:")) return rec.body;
  const resolved = await idb.get("meta", "photo_results") || {};
  return JSON.parse(txt.replace(/"local:([0-9a-f-]{36})"/g, (m, pid) => (resolved[pid] ? JSON.stringify(resolved[pid].storage_path) : m)));
}

/** FLD-OFF-FIX-3 §6 — a queue entry is checked BEFORE it is sent. A record that would arrive at
 *  the server with missing or null fields is quarantined (`malformed`) and surfaced in the Sync
 *  panel; it is never sent and never retried on its own. Empty-body writes that are legitimately
 *  empty stay legal — only the kinds that carry data are required to carry it. */
const DATA_KINDS = new Set(["completion", "placement", "return", "ticket", "punch", "readiness", "note", "markup"]);
const NULLISH = (v) => v === null || v === undefined || v === "null" || v === "undefined" || v === "";
export function validateRecord(rec) {
  if (!rec?.url || !rec?.method) return "the saved request is incomplete (no address)";
  const form = rec.body?.form;
  if (form) {
    if (rec.kind === "photo" && !form.some((p) => p.blob)) return "the photo file is not in this record";
    const bad = form.filter((p) => !p.blob && NULLISH(p.v)).map((p) => p.k);
    if (bad.length) return `these fields are empty: ${bad.join(", ")}`;
    if (rec.kind === "photo" && !form.some((p) => p.k === "project_id" && !NULLISH(p.v))) return "the project is missing from this record";
    return "";
  }
  const json = rec.body?.json;
  if (DATA_KINDS.has(rec.kind) && (json === null || json === undefined)) return "the saved change has no content";
  if (json && typeof json === "object" && !Array.isArray(json)) {
    const bad = Object.entries(json).filter(([, v]) => v === "null" || v === "undefined").map(([k]) => k);
    if (bad.length) return `these fields were lost: ${bad.join(", ")}`;
  }
  return "";
}

export async function replay(manual = false) {
  if (state.replaying || isManualOffline()) return;
  if (!state.online && !manual) return;
  if (manual && !state.online && !(await probe(clients.field))) return;
  const wifiOnly = await idb.get("meta", "wifi_only");
  state.replaying = true; emit();
  try {
    const due = (r) => {
      if (!RETRYABLE.includes(r.status)) return false;
      if (manual || r.manual) return true;
      // rejected by validation: the payload is a bug — retry only once the app changed
      if (r.permanent) return (r.failed_version || 0) < LOGIC_VERSION;
      return (r.nextAt || 0) <= Date.now();
    };
    const list = order(await dedupe((await outboxList()).filter(due)));
    for (const rec of list) {
      const client = clients[rec.client];
      if (!client) continue;
      if (rec.kind === "photo" && wifiOnly && navigator.connection?.type && navigator.connection.type !== "wifi") continue;
      const bad = validateRecord(rec);
      if (bad) {
        console.warn(`[outbox] quarantined ${rec.kind} ${rec.id}: ${bad}`);
        rec.status = "malformed"; rec.local_error = bad; rec.permanent = true;
        await idb.set("outbox", rec.id, rec); emit(); continue;
      }
      rec.status = "sending"; await idb.set("outbox", rec.id, rec); emit();
      try {
        const body = { ...rec, body: await resolveRefs(rec) };
        const data = await deserializeBody(body);
        const res = await client.request({ method: rec.method, url: rec.url, params: rec.params || undefined, data,
          headers: { "X-Client-Write-Id": rec.id, "X-Client-Timestamp": rec.client_timestamp, "X-Offline-Replay": "1",
            "X-Project-Id": rec.project_id, "X-Write-Kind": rec.kind,
            // FIX-2 §1: say multipart explicitly — the instance default is application/json, under
            // which axios serialises a FormData body to JSON and every Form field arrives missing
            "Content-Type": data instanceof FormData ? "multipart/form-data" : "application/json" },
          _replay: true, timeout: rec.kind === "photo" ? 60000 : 12000, validateStatus: () => true });   // a hung request becomes "failed" and retries, never a stuck "sending"
        noteOk(0);                                   // the server answered — the link is alive
        const ws0 = res.headers?.["x-write-status"] || (res.status < 300 ? "accepted" : res.status >= 500 ? "failed" : res.status === 409 ? "conflict" : "rejected");
        // A photo is only synced once the server confirms the FILE is stored — an accepted-looking
        // response with no storage_path is a retry, never a tick.
        let ws = (rec.kind === "photo" && ws0 === "accepted" && !res.data?.storage_path) ? "failed" : ws0;
        // a stale action against a record whose state moved on: decided here, never asked. A markup
        // aimed at a superseded sheet revision is one of these — the server's revision stands and no
        // installer can adjudicate it (the office still sees it in the conflict trail).
        if (ws === "superseded" || (ws === "conflict" && rec.kind === "markup" && /not waiting|current revision/i.test(plainDetail(res.data)))) {
          console.info(`[outbox] ${rec.kind} ${rec.id} superseded on the server (${plainDetail(res.data)}) — discarded`);
          await removeRecord(rec.id); continue;
        }
        // a validation refusal is a bug in the payload we built, not the installer's call
        if (ws === "rejected" || (ws === "conflict" && rec.kind === "photo")) {
          ws = "failed"; rec.permanent = true; rec.failed_version = LOGIC_VERSION;
        } else rec.permanent = false;
        rec.status = ws; rec.response = res.data; rec.code = res.status; rec.attempts += 1; rec.sent_at = new Date().toISOString();
        rec.manual = false; rec.local_error = "";
        rec.server_error = res.status >= 400 ? (plainDetail(res.data) || `the server answered ${res.status}`) : "";
        if (ws === "failed" && !rec.permanent) rec.nextAt = Date.now() + Math.min(300000, 2000 * 2 ** Math.min(rec.attempts, 8));
        if (ws === "failed" && rec.kind === "photo" && ws0 === "accepted") rec.local_error = "The server did not confirm the file was stored — retrying";
        if (rec.kind === "photo" && ws === "accepted") {
          const m = (await idb.get("meta", "photo_results")) || {}; m[rec.id] = res.data; await idb.set("meta", "photo_results", m);
          for (const p of rec.body?.form || []) if (p.blob) await idb.del("blobs", p.blob);   // the ONLY moment a pending file is released: the server confirmed it
        }
        if (ws === "accepted" && rec.kind !== "photo") { await idb.set("outbox", rec.id, rec); setTimeout(() => removeRecord(rec.id), 120000); }
        else await idb.set("outbox", rec.id, rec);
        if (["accepted", "adjusted"].includes(ws)) { await dropOptimistic(rec); await hook(rec, "onAccepted"); }
        else if (ws === "conflict") await hook(rec, "onConflict");
      } catch (e) {
        rec.attempts += 1; rec.manual = false;
        const verdict = classify(e);
        if (verdict === "netfail") noteFail({ critical: true, timeout: /timeout/i.test(e.message || ""), reason: `replay ${rec.kind}: ${e.message || e.code || ""}` });
        if (e.lost) {
          // the OS reclaimed the file — a LOCAL condition, reported as a loss, not as a conflict
          rec.status = "lost"; rec.local_error = "This photo file was removed from the device by the system before it could upload — it has to be taken again";
        } else { rec.status = "failed"; rec.permanent = false; rec.local_error = /timeout/i.test(e.message || "") ? "no answer from the server (timed out)" : (e.message === "Network Error" ? "no connection" : (e.message || "no connection")); }
        rec.nextAt = Date.now() + Math.min(300000, 2000 * 2 ** Math.min(rec.attempts, 8));
        await idb.set("outbox", rec.id, rec);
        // the link died mid-drain: stop hammering it, the timer + foreground wake will come back
        // the link really is down: stop hammering it. A slow or stalled app never lands here.
        if (!e.lost && isLinkDown()) { state.online = false; break; }
      }
      emit();
    }
  } finally { state.replaying = false; emit(); heartbeat(); }
}

export const isReplaying = () => state.replaying;

async function dropOptimistic(rec) {
  const key = `optimistic:${rec.project_id}`;
  const cur = (await idb.get("meta", key)) || [];
  await idb.set("meta", key, cur.filter((x) => x.id !== rec.id));
}

export async function heartbeat() {
  const c = clients.field;
  if (!c || !state.online) return;
  try {
    const list = await outboxList();
    const pend = list.filter((r) => WAITING.includes(r.status));
    await c.post("/offline/heartbeat", { device_id: await deviceId(), pending: pend.length,
      oldest_pending_at: pend[0]?.client_timestamp || "", conflicts: list.filter((r) => r.status === "conflict").length,
      storage_bytes: await storageEstimate() }, { _replay: true, _noQueue: true });
  } catch { /* best effort */ }
}

/** Every drain trigger except the one we cannot trust (the browser `online` event alone):
 *  a timer, app foreground, a good-signal probe and the manual "Sync now". */
if (typeof window !== "undefined") setInterval(() => { wake("timer"); enforceCap(); }, 20000);
